import { useEffect, useState } from "react";
import { deadlineOverride } from "../lib/utils";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import PublicBrowseShell from "../components/PublicBrowseShell";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tractor, ArrowSquareOut, Info, Calendar, MapPin, CheckCircle, Eye, X } from "@phosphor-icons/react";

const audienceLabel = {
  farmer: "Farmers",
  small_business: "Small Business",
  nonprofit: "Nonprofits",
};

const formatDeadline = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

const FarmerDigest = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDemoBanner, setShowDemoBanner] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/grants?audience=farmer&limit=100`);
        const all = res.data.grants || res.data || [];
        const farmerAg = (Array.isArray(all) ? all : []).filter(
          (g) => g.category === "Agriculture" && g.verification_status !== "needs_review"
        );
        // Sort by earliest verified deadline first
        farmerAg.sort((a, b) => {
          const ad = a.deadline || "9999-12-31";
          const bd = b.deadline || "9999-12-31";
          return ad.localeCompare(bd);
        });
        setGrants(farmerAg);
      } catch (e) {
        setGrants([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const Shell = user ? DashboardLayout : PublicBrowseShell;

  return (
    <Shell>
      <div className="max-w-6xl mx-auto space-y-8 pb-16" data-testid="farmer-digest-page">
        {/* DEMO PREVIEW BANNER — remove after Chris Kelly / BBOA pilot signs (2026-09-25) */}
        {showDemoBanner && (
          <div
            className="relative rounded-lg border border-emerald-500/40 bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent p-4 sm:p-5"
            data-testid="farmer-digest-demo-banner"
          >
            <div className="flex items-start gap-3 pr-8">
              <Eye weight="duotone" className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-zinc-300 leading-relaxed">
                <div className="text-xs font-semibold uppercase tracking-widest text-emerald-400 mb-1">
                  Live Preview &middot; BBOA-Branded Version
                </div>
                <p>
                  This is a working preview of what a{" "}
                  <span className="text-white font-semibold">BBOA-branded Farmer Funding Digest</span>{" "}
                  looks like on the FlashGrants platform. In the shipped version, the header, colors,
                  and welcome message swap to BBOA&rsquo;s brand &mdash; farmers see &ldquo;BBOA Farmer
                  Funding&rdquo; when they log in, not FlashGrants. The grants, verification, and
                  admin dashboard stay identical.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowDemoBanner(false)}
              className="absolute top-3 right-3 text-zinc-500 hover:text-white transition"
              aria-label="Dismiss preview banner"
              data-testid="farmer-digest-demo-banner-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Hero */}
        <div className="border-l-4 border-emerald-500 pl-6 py-2">
          <div className="flex items-center gap-2 text-emerald-500 text-xs font-semibold uppercase tracking-widest mb-2">
            <Tractor weight="duotone" className="w-4 h-4" />
            <span>Farmer Funding Digest &middot; Powered by FlashGrants</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-serif text-white mb-3" data-testid="farmer-digest-heading">
            Grant literacy for farmers.
          </h1>
          <p className="text-zinc-400 max-w-2xl text-base leading-relaxed">
            A curated feed of federal, state, and philanthropic funding built for
            beginning, veteran, limited-resource and small farmers &mdash; specialty
            crops, organic transition, and farmer cooperatives. Every program
            below links to its own official source. No aggregators. No guesses.
          </p>
        </div>

        {/* Trust callout */}
        <Card className="bg-emerald-500/5 border-emerald-500/30" data-testid="farmer-digest-callout">
          <CardContent className="p-5 flex items-start gap-4">
            <CheckCircle weight="duotone" className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-zinc-300 leading-relaxed">
              <span className="font-semibold text-emerald-400">Verified against primary sources.</span>{" "}
              Every listing links directly to <span className="font-mono text-white">usda.gov</span>,{" "}
              <span className="font-mono text-white">ams.usda.gov</span>,{" "}
              <span className="font-mono text-white">nifa.usda.gov</span>, or{" "}
              <span className="font-mono text-white">agriculture.sc.gov</span>. If you can't verify it there,
              it doesn't make it here.
            </div>
          </CardContent>
        </Card>

        {/* Headline wedge */}
        <Card className="bg-gradient-to-br from-emerald-900/20 to-zinc-950 border-emerald-500/40" data-testid="farmer-digest-headline">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center gap-2 text-emerald-400 text-xs font-semibold uppercase tracking-widest">
              <Calendar weight="duotone" className="w-4 h-4" />
              <span>The wedge this month</span>
            </div>
            <h2 className="text-2xl font-serif text-white leading-snug">
              Southern SARE Producer Grant (2027 cycle)
            </h2>
            <p className="text-sm text-zinc-300 leading-relaxed">
              Up to <span className="text-white font-semibold">$20,000 for an individual farmer</span> or{" "}
              <span className="text-white font-semibold">$25,000 for a farmer organization</span>, paid as
              reimbursement for a two-year on-farm project. Deadline{" "}
              <span className="text-white font-semibold">November 6, 2026 at noon ET</span>. Applicants need at
              least $1,000 in annual farm income and at least one cooperator.
            </p>
            <div className="pt-2">
              <a
                href="https://southern.sare.org/grants/apply-for-a-grant/producer-grants/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm text-emerald-400 hover:text-emerald-300 font-semibold"
                data-testid="farmer-digest-scmp-source"
              >
                Verify at southern.sare.org <ArrowSquareOut className="w-3.5 h-3.5" />
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Grants grid */}
        {loading ? (
          <p className="text-zinc-500 text-sm" data-testid="farmer-digest-loading">Loading the digest...</p>
        ) : grants.length === 0 ? (
          <Card className="bg-zinc-950 border-zinc-800">
            <CardContent className="p-8 text-center text-zinc-500">
              No verified farmer programs loaded yet.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="farmer-digest-grid">
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
              {grants.length} verified programs
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {grants.map((g) => (
                <Card
                  key={g.grant_id || g.id}
                  className="bg-zinc-950 border-zinc-800 hover:border-emerald-500/50 transition"
                  data-testid={`farmer-grant-card-${g.grant_id}`}
                >
                  <CardContent className="p-6 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-serif text-white leading-snug mb-1">
                          {g.title}
                        </h3>
                        <p className="text-xs text-zinc-500 font-medium">{g.funder_name}</p>
                      </div>
                      <Tractor weight="duotone" className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                    </div>

                    <p className="text-sm text-zinc-400 leading-relaxed line-clamp-4">
                      {g.description}
                    </p>

                    {/* Meta row */}
                    <div className="flex flex-wrap gap-2 pt-2">
                      {g.amount_max ? (
                        <span className="text-xs px-2 py-1 rounded bg-zinc-900 border border-zinc-800 text-emerald-400 font-mono">
                          Up to ${g.amount_max.toLocaleString()}
                        </span>
                      ) : null}
                      {g.deadline ? (
                        <span className="text-xs px-2 py-1 rounded bg-zinc-900 border border-zinc-800 text-amber-400 font-mono">
                          {deadlineOverride(g) || `Due ${formatDeadline(g.deadline)}`}
                        </span>
                      ) : null}
                      {(g.audience || []).map((a) => (
                        <span
                          key={a}
                          className="text-xs px-2 py-1 rounded bg-zinc-900 border border-zinc-800 text-zinc-400"
                        >
                          {audienceLabel[a] || a}
                        </span>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3 pt-3">
                      <Button
                        size="sm"
                        onClick={() => navigate(`/grants/${g.grant_id || g.id}`)}
                        className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"
                        data-testid={`farmer-grant-details-${g.grant_id}`}
                      >
                        See details
                      </Button>
                      {g.application_url ? (
                        <a
                          href={g.application_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm text-zinc-400 hover:text-emerald-400 flex items-center gap-1"
                          data-testid={`farmer-grant-external-${g.grant_id}`}
                        >
                          Primary source <ArrowSquareOut className="w-3.5 h-3.5" />
                        </a>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Footer note */}
        <Card className="bg-zinc-950 border-zinc-800" data-testid="farmer-digest-footer">
          <CardContent className="p-5 flex items-start gap-4">
            <MapPin weight="duotone" className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-zinc-400 leading-relaxed">
              Need capital that a grant can't cover?{" "}
              <Link to="/capital" className="text-amber-400 hover:text-amber-300 font-semibold underline">
                Browse mission-driven lenders
              </Link>
              . Not a farmer?{" "}
              <Link to="/grants" className="text-emerald-400 hover:text-emerald-300 font-semibold underline">
                Browse the full grants catalog
              </Link>
              .
            </div>
          </CardContent>
        </Card>
      </div>
    </Shell>
  );
};

export default FarmerDigest;
